import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { components } from "@/lib/openapi";
import {
  useBaggageHalls,
  useOAGBaggageCounters,
  useUpdateBaggageCarousel,
} from "@/queries/baggage";
import { getCarriers } from "@/queries/landing";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Pencil } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

type BaggageCarouselSchema = components["schemas"]["BaggageCarouselSchema"];

const updateCarouselFormSchema = z.object({
  name: z.string().min(1, "Carousel name is required"),
  identifiers: z.array(z.string()).default([]),
  preferred_carriers: z.array(z.string()).default([]),
});

type UpdateCarouselFormValues = z.infer<typeof updateCarouselFormSchema>;

interface UpdateBaggageCarouselDialogProps {
  carousel: BaggageCarouselSchema;
}

export function UpdateBaggageCarouselDialog({
  carousel,
}: UpdateBaggageCarouselDialogProps) {
  const [open, setOpen] = useState(false);
  const { data: baggageHalls } = useBaggageHalls();
  const { data: carriers, isLoading: loadingCarriers } = getCarriers();
  const { data: oagCounters, isLoading: loadingOAGCounters } =
    useOAGBaggageCounters();
  const updateMutation = useUpdateBaggageCarousel();

  const carrierOptions = useMemo(() => {
    if (!carriers) return [];
    return carriers.map((carrier) => ({
      value: carrier.code,
      label: `${carrier.name} (${carrier.code})`,
    }));
  }, [carriers]);

  const identifierOptions = useMemo(() => {
    if (!oagCounters) return [];
    return oagCounters.map((counter) => ({
      value: counter,
      label: counter,
    }));
  }, [oagCounters]);

  const form = useForm<UpdateCarouselFormValues>({
    resolver: zodResolver(updateCarouselFormSchema),
    defaultValues: {
      name: carousel.name,
      identifiers: carousel.identifiers || [],
      preferred_carriers: carousel.preferred_carriers || [],
    },
  });

  const baggageHallName = useMemo(() => {
    if (!baggageHalls) return `ID: ${carousel.baggage_hall_id}`;
    const hall = baggageHalls.find((h) => h.id === carousel.baggage_hall_id);
    return hall?.name || `ID: ${carousel.baggage_hall_id}`;
  }, [baggageHalls, carousel.baggage_hall_id]);

  useEffect(() => {
    if (open && carousel) {
      form.reset({
        name: carousel.name,
        identifiers: carousel.identifiers || [],
        preferred_carriers: carousel.preferred_carriers || [],
      });
    } else if (!open) {
      form.reset();
    }
  }, [open, carousel, form.reset]);

  const handleSubmit = (values: UpdateCarouselFormValues) => {
    const payload = {
      id: carousel.id,
      ...values,
    };
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setOpen(false);
      },
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      updateMutation.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Baggage Carousel</DialogTitle>
          <DialogDescription>
            Update the baggage carousel configuration.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Carousel 1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Baggage Hall</FormLabel>
              <FormControl>
                <Input value={baggageHallName} disabled />
              </FormControl>
            </FormItem>

            <FormField
              control={form.control}
              name="identifiers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>OAG Identifiers</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={identifierOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select OAG identifiers"
                      disabled={loadingOAGCounters}
                      creatable
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="preferred_carriers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Carriers (Optional)</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={carrierOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select carriers"
                      disabled={loadingCarriers}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {updateMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                <span>
                  {updateMutation.error?.message ||
                    "Failed to update baggage carousel. Please try again."}
                </span>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Updating..." : "Update Carousel"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
